/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {
  Account,
  Entity
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {titleCase} from '@ion/core/src/utils/StringUtil';
import {useShowDeletePrompt} from '@ion/dls/src/hooks/useShowDeletePrompt';
import {DeleteResultPrompt} from '@ion/features/src/prompts/DeleteResultPrompt';
import DeletePrompt from './DeletePrompt';

// defines the GroupsDeletePrompt Prop type definitions ...
export interface GroupsDeletePromptProps {
  show: boolean;
  group: Account | null;
  overrideMessage?: Function | null;
  entity?: string;
  result?: Account | null;
  loading?: boolean;
  error?: ServiceError | null;
  isCtcAdmin?: boolean;
  onDeleteConfirm: (group: Account) => void;
  onDeleteCancel: () => void;
};

/**
 * @GroupsDeletePrompt
 *
 * The GroupsDeletePrompt provides the UI for prompting, confirming, or canceling an Group
 * Delete action.
 *
 * ```typescript
 *
 *    import {GroupsDeletePrompt} from '@ion/dls/src/editors/GroupsDeletePrompt';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({adminGroups: {destroy}}) => destroy);
 *
 *    const onDeleteConfirm = function(group: Account) {
 *      dispatch(deleteGroup(selected));
 *    };
 *
 *    function onDeleteCancel() {
 *      dispatch(deleteAdminGroup(selected, false));
 *    }
 *
 *    return (
 *      <GroupsDeletePrompt show={isEditing}
 *        group={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the group delete prompt will be displayed.
 * @param {Account}       group The selected Account that is to be deleted.
 * @param {Account}       result The Account returned by the service after it has been deleted.
 * @param {boolean}       loading When true, indicates that the group is currently being
 *                        deleted.
 * @param {ServiceError}  error A reference to the ServiceError If the deletion fails.
 * @param {Function}      onDeleteConfirm callback function which will be invoked with the
 *                        group when the user confirms the deletion.
 * @param {Function}      onDeleteCancel callback function which will be invoked if the user
 *                        cancels the delete operation.
 */
export const GroupsDeletePrompt = ({
  show,
  group,
  result,
  loading,
  error,
  overrideMessage = null,
  isCtcAdmin = false,
  onDeleteConfirm,
  onDeleteCancel
}: GroupsDeletePromptProps) => {
  const entityName = isCtcAdmin ? Entity.account : Entity.group;
  const {setShowError, setShowSuccess, showSuccess, showError, closeSnack} = useShowDeletePrompt();

  useEffect(() => {
    setShowError(!!error);
  }, [error]);

  useEffect(() => {
    if (result && !loading) {
      setShowSuccess(true);
    }
  }, [result]);

  return (
    <>
      {show && (
        <DeletePrompt show={show}
          entity={entityName}
          items={group}
          isLoading={loading}
          overrideMessage={overrideMessage}
          onConfirm={function() {
            onDeleteConfirm(group);
          }}
          onCancel={function() {
            onDeleteCancel();
          }} />
      )}
      <DeleteResultPrompt entityTitle={titleCase(entityName)}
        error={error}
        isError={showError}
        isSuccess={showSuccess}
        onAction={closeSnack} />
    </>
  );
};

export default GroupsDeletePrompt;
